import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaceValueCard } from './PlaceValueCard';
import { VsjMapReadingState } from './VsjMapReadingState';
import type { PlaceValueResponse } from './place-value-api';
import {
  getPublicPopulationStatus,
  getPublicPopulationGeoJson,
  type PopulationMethod,
  type PopulationStatus,
  type PopulationGeoJson,
} from './public-population-api';
import { fetchMapPlace, type MapPlaceReading } from './vsj-map-place-api';
import { showSelectedPlace, clearSelectedPlace } from './vsj-selected-place-layer';

const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined) ?? '';
const BASEMAP_URL = `https://api.maptiler.com/maps/dataviz-light/style.json?key=${MAPTILER_KEY}`;

const VSJ_CENTER: [number, number] = [-68.5354, -31.5375];
const VSJ_ZOOM = 12;
const VSJ_SLUG = 'valor-san-juan';

const POP_SOURCE = 'vsj-population-segments-source';
const POP_LAYER = 'vsj-population-segments-layer';

// P95 = 90.3 from data inspection; used to calibrate the color ramp
const POP_COLOR_STOPS = [0, 15, 40, 70, 90] as const;
// Sober slate ramp: the residential reading stays in the background;
// the strongest contrast on the map is reserved for the selected cuadra.
const POP_COLOR_COLORS = ['#dbe4ee', '#b3c3d9', '#87a0c0', '#5a739f', '#2d3561'] as const;

// Public labels for the population methods — internal ids stay L1/L2.
const METHOD_LABELS: Record<PopulationMethod, string> = {
  building_refined: 'Densidad residencial',
  census_network_length: 'Cobertura territorial',
};

const METHOD_NOTES: Record<PopulationMethod, string> = {
  building_refined: 'La red ayuda a entender dónde se concentra la vida cotidiana.',
  census_network_length: 'Lectura construida a partir de población, hogares y red urbana.',
};

type PanelState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | {
      kind: 'result';
      data: PlaceValueResponse;
      lng: number;
      lat: number;
      reading: MapPlaceReading | null;
    };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildPopColorExpr(): any {
  const stops: unknown[] = [];
  for (let i = 0; i < POP_COLOR_STOPS.length; i++) {
    stops.push(POP_COLOR_STOPS[i], POP_COLOR_COLORS[i]);
  }
  return ['interpolate', ['linear'], ['coalesce', ['get', 'population_estimated'], 0], ...stops];
}

function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('es-AR');
}

function fmtPct(n: number): string {
  return n.toLocaleString('es-AR', { maximumFractionDigits: 1 });
}

/** Quiet dot with a breathing ring — the exploration map's own marker, no default teardrop. */
function createPlaceMarker(map: maplibregl.Map, lng: number, lat: number): maplibregl.Marker {
  const el = document.createElement('div');
  el.className = 'vsj-xmarker';
  el.innerHTML = '<span class="vsj-xmarker-ring"></span><span class="vsj-xmarker-dot"></span>';
  return new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
}

// eslint-disable-next-line max-lines-per-function, complexity
export function VsjMapSection(): JSX.Element {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const clickCounterRef = useRef(0);

  const [mapReady, setMapReady] = useState(false);
  const [panel, setPanel] = useState<PanelState>({ kind: 'idle' });

  // ── Population state ──────────────────────────────────────────────────────────

  const [populationActive, setPopulationActive] = useState(true);
  const [populationMethod, setPopulationMethod] = useState<PopulationMethod>('building_refined');
  const [populationStatus, setPopulationStatus] = useState<PopulationStatus | null>(null);
  const [populationGeoJson, setPopulationGeoJson] = useState<PopulationGeoJson | null>(null);
  const [populationLoading, setPopulationLoading] = useState(false);
  const [populationError, setPopulationError] = useState<string | null>(null);

  // ── Map init ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAP_URL,
      center: VSJ_CENTER,
      zoom: VSJ_ZOOM,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), 'top-right');

    map.on('load', () => setMapReady(true));

    return () => {
      abortRef.current?.abort();
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ── Population data loading ───────────────────────────────────────────────────

  useEffect(() => {
    if (!populationActive) return;

    setPopulationLoading(true);
    setPopulationError(null);
    setPopulationStatus(null);
    setPopulationGeoJson(null);

    let cancelled = false;

    void Promise.all([
      getPublicPopulationStatus(VSJ_SLUG, populationMethod),
      getPublicPopulationGeoJson(VSJ_SLUG, populationMethod),
    ])
      .then(([status, geojson]) => {
        if (cancelled) return;
        setPopulationStatus(status);
        setPopulationGeoJson(geojson);
      })
      .catch(() => {
        if (cancelled) return;
        setPopulationError('No se pudo cargar la capa de población.');
      })
      .finally(() => {
        if (!cancelled) setPopulationLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [populationActive, populationMethod]);

  // ── Population layer management ───────────────────────────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (map.getLayer(POP_LAYER)) map.removeLayer(POP_LAYER);
    if (map.getSource(POP_SOURCE)) map.removeSource(POP_SOURCE);

    if (!populationActive || !populationGeoJson) return;

    map.addSource(POP_SOURCE, {
      type: 'geojson',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: populationGeoJson as any,
    });

    map.addLayer({
      id: POP_LAYER,
      type: 'line',
      source: POP_SOURCE,
      paint: {
        'line-color': buildPopColorExpr(),
        'line-width': 2,
        'line-opacity': 0.55,
        'line-blur': 0.3,
      },
    });

    return () => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(POP_LAYER)) m.removeLayer(POP_LAYER);
        if (m.getSource(POP_SOURCE)) m.removeSource(POP_SOURCE);
      } catch (_e) {
        /* ignore if map already destroyed */
      }
    };
  }, [mapReady, populationActive, populationGeoJson]);

  // ── Click handler ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const canvas = map.getCanvas();

    const onClick = (e: maplibregl.MapMouseEvent): void => {
      const { lng, lat } = e.lngLat;

      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        markerRef.current = createPlaceMarker(map, lng, lat);
      }

      // The previous cuadra light goes out the moment a new place is chosen
      clearSelectedPlace(map);

      // Cancel any in-flight request from a previous click
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      clickCounterRef.current += 1;
      const myClickId = clickCounterRef.current;

      setPanel({ kind: 'loading' });

      void fetchMapPlace(lng, lat, abortRef.current.signal)
        .then(result => {
          if (clickCounterRef.current !== myClickId) return;
          const geometry = result.reading?.confirmedGeometry;
          if (geometry && mapRef.current) {
            showSelectedPlace(mapRef.current, geometry);
          }
          setPanel({
            kind: 'result',
            data: result.valueResponse,
            lng: result.lng,
            lat: result.lat,
            reading: result.reading,
          });
        })
        .catch(err => {
          if (clickCounterRef.current !== myClickId) return;
          if ((err as Error).name === 'AbortError') return;
          setPanel({
            kind: 'result',
            lng,
            lat,
            reading: null,
            data: {
              status: 'insufficient_data',
              place: {
                scopeLabel: 'Entorno cercano (500 m)',
                contextMethod: 'radial_500m',
                contextRadiusM: 500,
              },
              messages: {
                title: 'Valores de referencia del mercado',
                summary: 'No se pudo obtener información para este punto. Intentá de nuevo.',
                disclaimer:
                  'Los valores son referencias construidas a partir de avisos publicados. No representan una tasación ni operaciones efectivamente cerradas.',
              },
            },
          });
        });
    };

    const onMouseEnter = (): void => {
      canvas.style.cursor = 'crosshair';
    };
    const onMouseLeave = (): void => {
      canvas.style.cursor = '';
    };

    map.on('click', onClick);
    map.on('mouseenter', onMouseEnter);
    map.on('mouseleave', onMouseLeave);

    return () => {
      map.off('click', onClick);
      map.off('mouseenter', onMouseEnter);
      map.off('mouseleave', onMouseLeave);
    };
  }, [mapReady]);

  function handleClosePanel(): void {
    setPanel({ kind: 'idle' });
    markerRef.current?.remove();
    markerRef.current = null;
    if (mapRef.current) clearSelectedPlace(mapRef.current);
  }

  function handleAction(action: string): void {
    if (action !== 'tasar' || panel.kind !== 'result') return;
    const address = panel.reading?.streetName ?? 'Lugar seleccionado en el mapa';
    navigate('/p/valor-san-juan/asistente', {
      state: {
        preloadedPlace: {
          id: `map-${panel.lng.toFixed(4)}_${panel.lat.toFixed(4)}`,
          address,
          street: address,
          block: '',
          neighborhood: '',
          city: 'San Juan',
          lat: panel.lat,
          lng: panel.lng,
        },
      },
    });
  }

  function handleTogglePopulation(): void {
    setPopulationActive(v => !v);
  }

  function handleSelectMethod(method: PopulationMethod): void {
    if (method !== populationMethod) {
      setPopulationMethod(method);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const panelOpen = panel.kind !== 'idle';
  const showLegend =
    populationActive && !populationLoading && populationStatus != null && populationError == null;
  const showPopError = populationActive && populationError != null;

  return (
    <div className="vsj-map-section">
      <div className="vsj-map-inner">
        {/* Map container */}
        <div className="vsj-map-wrap">
          <div ref={containerRef} className="vsj-map-canvas" />

          {/* Urban environment controls */}
          <div className="vsj-pop-controls">
            <button
              className={`vsj-pop-toggle${populationActive ? ' vsj-pop-toggle--active' : ''}`}
              onClick={handleTogglePopulation}
              aria-pressed={populationActive}
              aria-label="Entorno urbano"
            >
              <span>Entorno urbano</span>
              {populationLoading && populationActive && (
                <span className="vsj-pop-spinner" aria-hidden="true" />
              )}
            </button>

            {populationActive && (
              <div className="vsj-pop-method-select" role="group" aria-label="Modo de lectura">
                <button
                  className={`vsj-pop-method-btn${populationMethod === 'building_refined' ? ' vsj-pop-method-btn--active' : ''}`}
                  onClick={() => handleSelectMethod('building_refined')}
                  disabled={populationLoading}
                >
                  {METHOD_LABELS.building_refined}
                </button>
                <button
                  className={`vsj-pop-method-btn${populationMethod === 'census_network_length' ? ' vsj-pop-method-btn--active' : ''}`}
                  onClick={() => handleSelectMethod('census_network_length')}
                  disabled={populationLoading}
                >
                  {METHOD_LABELS.census_network_length}
                </button>
              </div>
            )}
          </div>

          {/* Residential reading legend */}
          {showLegend && populationStatus != null && (
            <div className="vsj-pop-legend" aria-label="Lectura del entorno urbano">
              <div className="vsj-pop-legend-title">Cómo se distribuye la residencia</div>
              <div className="vsj-pop-legend-sub">{METHOD_LABELS[populationMethod]}</div>
              <div className="vsj-pop-legend-bar" aria-hidden="true" />
              <div className="vsj-pop-legend-labels" aria-hidden="true">
                <span>menor</span>
                <span>mayor</span>
              </div>
              <div className="vsj-pop-legend-metrics">
                <div>
                  <strong>{fmtNum(populationStatus.totalPopulationAssigned)}</strong> personas
                  leídas
                </div>
                <div>
                  <strong>{fmtNum(populationStatus.totalHouseholdsAssigned)}</strong> hogares
                  estimados
                </div>
                <div>
                  <strong>{fmtPct(populationStatus.assignmentPct)}%</strong> de cobertura urbana
                </div>
              </div>
              <div className="vsj-pop-legend-note">{METHOD_NOTES[populationMethod]}</div>
            </div>
          )}

          {/* Population error */}
          {showPopError && (
            <div className="vsj-pop-error" role="alert">
              {populationError}
            </div>
          )}

          {/* Hint */}
          {!panelOpen && (
            <div className="vsj-map-hint" aria-hidden="true">
              Tocá una cuadra para leer su entorno
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className={`vsj-map-panel${panelOpen ? ' vsj-map-panel--open' : ''}`}>
          {panel.kind === 'loading' && <VsjMapReadingState />}

          {panel.kind === 'result' && (
            <PlaceValueCard
              data={panel.data}
              reading={panel.reading}
              onClose={handleClosePanel}
              onAction={handleAction}
            />
          )}
        </div>
      </div>
    </div>
  );
}
